import { Divider } from "@affinity/components/Divider";
import { emailValidator, phoneValidator } from "@affinity/components/validators";
import { rem } from "@affinity/constants/StyleHelpers";
import createAccount from "@affinity/Handlers/CreateAccount";
import { CustomState } from "@affinity/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { Checkbox, Typography } from "@insite/mobius/index";
import MobiusLink from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: CustomState) => ({
    // Map required state elements to component properties
});

const mapDispatchToProps = {
    // Map loader and setter methods to component properties
    createAccount,
    validatePassword,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const Step1PersonalDetails: React.FC<Props> = ({ validatePassword, createAccount }) => {
    const [state, setState] = React.useState({
        firstName: "",
        lastName: "",
        contactNumber: "",
        emailAddress: "",
        password: "",
        confirmPassword: "",
        confirmedTermsAndConditions: false,
    });
    const [statusError, setStatusError] = React.useState("");
    const [error, setError] = React.useState({
        firstName: "",
        lastName: "",
        contactNumber: "",
        emailAddress: "",
        password: "",
        confirmPassword: "",
        confirmedTermsAndConditions: "",
    });
    const [isSubmitted, setIsSubmitted] = React.useState(false);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { target } = event;
        const propertyName = target.name;
        setState({
            ...state,
            [propertyName]: target.value,
        });
    };

    const validateInputChange =
        (validator: (val: string) => string | Promise<string>) =>
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            handleInputChange(event);
            const {
                target: { value, name: propertyName },
            } = event;
            const error = await validator(value);
            setError(e => ({
                ...e,
                [propertyName]: error,
            }));
        };

    const validateConfirmPassword = (newConfirmPassword: string) => {
        const errorMessage =
            state.password !== newConfirmPassword
                ? siteMessage("CreateNewAccountInfo_PasswordCombination_DoesNotMatch").toString()
                : "";
        return errorMessage;
    };

    const passwordValidator = (newPassword: string) => {
        return new Promise<string>(resolve => {
            validatePassword({
                password: newPassword,
                onComplete: errorMessage => {
                    resolve(errorMessage?.toString() || "");
                },
            });
        });
    };

    function submit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        setIsSubmitted(true);
        if (Object.values(error).find(e => e !== "")) {
            return;
        }
        setStatusError("");
        createAccount({
            ...state,
            onError: props => {
                setStatusError(props.statusError);
            },
        });
    }

    return (
        <>
            <StyledForm onSubmit={submit}>
                <Typography variant="h3">Set up your personal and sign-in details</Typography>
                <TextField
                    label="First name"
                    type="text"
                    name="firstName"
                    required
                    value={state.firstName}
                    onChange={handleInputChange}
                />
                <TextField
                    label="Last name"
                    type="text"
                    name="lastName"
                    required
                    value={state.lastName}
                    onChange={handleInputChange}
                />
                <TextField
                    label="Contact number"
                    type="tel"
                    name="contactNumber"
                    required
                    value={state.contactNumber}
                    onChange={validateInputChange(phoneValidator)}
                    error={isSubmitted && error.contactNumber}
                />
                <TextField
                    label="Email address"
                    type="email"
                    name="emailAddress"
                    required
                    value={state.emailAddress}
                    onChange={validateInputChange(emailValidator)}
                    error={isSubmitted && error.emailAddress}
                />
                <TextField
                    label={translate("Password")}
                    type="password"
                    name="password"
                    required
                    value={state.password}
                    onChange={validateInputChange(passwordValidator)}
                    error={isSubmitted && error.password}
                />
                <TextField
                    label={translate("Confirm Password")}
                    type="password"
                    name="confirmPassword"
                    required
                    value={state.confirmPassword}
                    onChange={validateInputChange(validateConfirmPassword)}
                    error={isSubmitted && error.confirmPassword}
                />
                <Checkbox
                    checked={state.confirmedTermsAndConditions}
                    onChange={e => {
                        setState(formdata => ({
                            ...formdata,
                            confirmedTermsAndConditions: !formdata.confirmedTermsAndConditions,
                        }));
                    }}
                >
                    Check this box to agree to our{" "}
                    <MobiusLink className="link" href="/termsandconditions">
                        Terms and Conditions
                    </MobiusLink>
                    .<sup>*</sup>
                </Checkbox>
                <Divider />
                <Button
                    type="submit"
                    variant="primary"
                    sizeVariant="large"
                    icon={{ position: "right", src: "ChevronRight" }}
                    css={css`
                        align-self: end;
                    `}
                    disabled={!state.confirmedTermsAndConditions}
                >
                    Continue
                </Button>
            </StyledForm>
            {statusError && (
                <Modal headline={statusError} isOpen={true} handleClose={() => setStatusError("")}>
                    <StyledWrapper>
                        <Typography>
                            The email address <strong>{state.emailAddress}</strong> is already in use. Please login
                            using this email or register with a different email address.
                        </Typography>
                    </StyledWrapper>
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Step1PersonalDetails);

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${rem(20)};
    gap: ${rem(30)};
`;
